/**
 * NavBadge — Notification badge for navigation items
 *
 * Reuses cached thread data from dashboard.
 * No new API calls, no polling.
 */
import React from 'react';

const COLORS = {
  red: 'bg-red-500 text-white',
  amber: 'bg-amber-500 text-white',
  grey: 'bg-slate-400 text-white',
  indigo: 'bg-indigo-500 text-white',
};

export const NavBadge = ({ count, color = 'red', testId }) => {
  if (!count || count <= 0) return null;
  const display = count > 99 ? '99+' : count;
  return (
    <span
      className={`inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[9px] font-bold leading-none ${COLORS[color] || COLORS.red}`}
      data-testid={testId}
    >
      {display}
    </span>
  );
};

/**
 * Compute badge counts from cached threads.
 * Same logic as buildSuggestions in CommandPalette.
 */
const SLA_THRESHOLD_HOURS = 48;

export const computeBadgeCounts = (threads, isAdmin, userId) => {
  const now = Date.now();
  const thresholdMs = SLA_THRESHOLD_HOURS * 60 * 60 * 1000;

  // B-3 / INV-A9 — canonical thread projection field is
  // `assigned_agent_id`. Legacy `assigned_to` retired.
  const visible = threads.filter(t => {
    if (isAdmin) return true;
    return !t.assigned_agent_id || t.assigned_agent_id === userId;
  });

  const escalated = visible.filter(t => t.status === 'escalated').length;
  const guardRisk = visible.filter(t => t.circumvention_flag === true).length;

  const slaBreach = visible.filter(t => {
    if (t.lead_status === 'WON' || t.lead_status === 'LOST') return false;
    if (t.first_response_at) return false;
    const created = t.created_at ? new Date(t.created_at).getTime() : 0;
    return created > 0 && (now - created) > thresholdMs;
  }).length;

  const unassigned = isAdmin ? visible.filter(t =>
    !t.assigned_agent_id && t.lead_status && t.lead_status !== 'WON' && t.lead_status !== 'LOST'
  ).length : 0;

  return { escalated, guardRisk, slaBreach, unassigned };
};

export default NavBadge;
